import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  AttachAuditVendorAccountsDto,
  AttachAuditVendorAccountsResultDto,
  AuditVendorAccountListDto,
  DetachAuditVendorAccountsDto,
  DetachAuditVendorAccountsResultDto,
  QueryKey,
  SelectListDto,
  UpdateVendorAccountDto,
  VendorAccountDto,
  VendorAccountListDto,
} from '@sib/api';
import {
  mapSelectListToOptions,
  type SelectOption,
} from '../utils/select-list';

const CACHE_TIME = 5000;

type SelectListOptionsQuery = Partial<
  UseQueryOptions<SelectListDto, Error, SelectOption[]>
>;

export type AuditVendorAccountsListQuery = NonNullable<
  Parameters<typeof api.internalPortal.auditVendorAccountsControllerList>[2]
>;

export type AuditVendorAccountsPickerListQuery = NonNullable<
  Parameters<
    typeof api.internalPortal.auditVendorAccountsControllerListPicker
  >[2]
>;

export type OrganizationVendorAccountsListQuery = NonNullable<
  Parameters<
    typeof api.internalPortal.vendorAccountsControllerGetVendorAccounts
  >[1]
>;

export const useAuditVendorAccountsList = (
  auditId: string,
  organizationId: string,
  query: AuditVendorAccountsListQuery,
  options?: Partial<UseQueryOptions<AuditVendorAccountListDto>>
) =>
  useQuery({
    ...options,
    enabled: !!auditId && !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.AuditVendorAccounts,
      auditId,
      organizationId,
      query,
      ...(options?.queryKey || []),
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.auditVendorAccountsControllerList(
        auditId,
        organizationId,
        query
      ),
  });

interface UseAuditVendorAccountsPickerListProps {
  auditId: string;
  organizationId: string;
  query: AuditVendorAccountsPickerListQuery;
  options?: Partial<UseQueryOptions<AuditVendorAccountListDto>>;
}

export const useAuditVendorAccountsPickerList = ({
  auditId,
  organizationId,
  query,
  options,
}: UseAuditVendorAccountsPickerListProps) =>
  useQuery({
    ...options,
    enabled: !!auditId && !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.AuditVendorAccounts,
      auditId,
      organizationId,
      'picker',
      query,
      ...(options?.queryKey || []),
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.auditVendorAccountsControllerListPicker(
        organizationId,
        auditId,
        query
      ),
  });

interface UseOrganizationVendorAccountsListProps {
  organizationId: string;
  query: OrganizationVendorAccountsListQuery;
  options?: Partial<UseQueryOptions<VendorAccountListDto>>;
}

export const useOrganizationVendorAccountsList = ({
  organizationId,
  query,
  options,
}: UseOrganizationVendorAccountsListProps) =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.VendorAccounts,
      organizationId,
      query,
      ...(options?.queryKey || []),
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorAccountsControllerGetVendorAccounts(
        organizationId,
        query
      ),
  });

export const useOrganizationVendorAccountsSelectList = (
  organizationId: string,
  options?: SelectListOptionsQuery
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.InternalPortalVendorAccountsSelectList,
      organizationId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.internalPortal.vendorAccountsControllerGetVendorAccountSelectList(
        organizationId
      ),
    select: mapSelectListToOptions,
  });

export const invalidateAuditVendorAccountQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  auditId: string,
  organizationId: string
) => {
  queryClient.invalidateQueries({
    queryKey: [QueryKey.Resource.AuditVendorAccounts, auditId, organizationId],
  });
  queryClient.invalidateQueries({
    queryKey: [QueryKey.Resource.VendorAccounts, organizationId],
  });
  queryClient.invalidateQueries({
    queryKey: [
      QueryKey.Resource.OrganizationAudits,
      QueryKey.Operation.One,
      organizationId,
      auditId,
    ],
  });
};

interface AttachAuditVendorAccountsPayload {
  auditId: string;
  organizationId: string;
  data: AttachAuditVendorAccountsDto;
}

interface DetachAuditVendorAccountsPayload {
  auditId: string;
  organizationId: string;
  data: DetachAuditVendorAccountsDto;
}

export const useAttachAuditVendorAccounts = (
  options?: UseMutationOptions<
    AttachAuditVendorAccountsResultDto,
    Error,
    AttachAuditVendorAccountsPayload
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ auditId, organizationId, data }) =>
      api.internalPortal.auditVendorAccountsControllerAttach(
        auditId,
        organizationId,
        data
      ),
    onSuccess: (data, variables, context) => {
      invalidateAuditVendorAccountQueries(
        queryClient,
        variables.auditId,
        variables.organizationId
      );

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useDetachAuditVendorAccounts = (
  options?: UseMutationOptions<
    DetachAuditVendorAccountsResultDto,
    Error,
    DetachAuditVendorAccountsPayload
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ auditId, organizationId, data }) =>
      api.internalPortal.auditVendorAccountsControllerDetach(
        auditId,
        organizationId,
        data
      ),
    onSuccess: (data, variables, context) => {
      invalidateAuditVendorAccountQueries(
        queryClient,
        variables.auditId,
        variables.organizationId
      );

      options?.onSuccess?.(data, variables, context);
    },
  });
};

interface UpdateVendorAccountPayload {
  organizationId: string;
  accountId: string;
  data: UpdateVendorAccountDto;
  auditId?: string;
}

export const useUpdateVendorAccount = (
  options?: UseMutationOptions<
    VendorAccountDto,
    Error,
    UpdateVendorAccountPayload
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ organizationId, accountId, data }) =>
      api.internalPortal.vendorAccountsControllerUpdateVendorAccount(
        organizationId,
        accountId,
        data
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.VendorAccounts, variables.organizationId],
      });

      if (variables.auditId) {
        invalidateAuditVendorAccountQueries(
          queryClient,
          variables.auditId,
          variables.organizationId
        );
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
};
